import React from "react";
import { Helmet } from "react-helmet-async";
import { useSection } from "../../context/SiteContentContext";

const DEFAULT_TITLE = "Skovomax — From Sensors to Systems Intelligence";
const DEFAULT_DESCRIPTION =
    "Skovomax builds photonic sensing platforms, LiDAR systems, fiber lasers and GeoAI analytics — integrated into real-world decision systems for industry, government and research.";

function absoluteUrl(urlOrPath) {
    if (!urlOrPath) return "";
    if (/^https?:\/\//i.test(urlOrPath)) return urlOrPath;
    if (typeof window === "undefined") return urlOrPath;
    return `${window.location.origin}${urlOrPath.startsWith("/") ? "" : "/"}${urlOrPath}`;
}

/**
 * SEO — per-page meta tags (title, description) + Open Graph + Twitter Card.
 *
 *   <SEO title="Technologies — Skovomax" description="..." image="/og/tech.png" />
 *
 * Falls back to sensible site-wide defaults from the Settings CMS (og_image_url).
 */
export default function SEO({
    title,
    description,
    image,
    type = "website",
    jsonLd,
}) {
    const settings = useSection("settings", {});
    const pageTitle = title || DEFAULT_TITLE;
    const pageDescription = description || DEFAULT_DESCRIPTION;
    const pageImage = absoluteUrl(image || settings?.og_image_url || "");
    const canonical =
        typeof window !== "undefined" ? window.location.href.split("?")[0].split("#")[0] : "";

    return (
        <Helmet>
            <title>{pageTitle}</title>
            <meta name="description" content={pageDescription} />
            {canonical ? <link rel="canonical" href={canonical} /> : null}

            {/* Open Graph */}
            <meta property="og:type" content={type} />
            <meta property="og:site_name" content="Skovomax" />
            <meta property="og:title" content={pageTitle} />
            <meta property="og:description" content={pageDescription} />
            {canonical ? <meta property="og:url" content={canonical} /> : null}
            {pageImage ? <meta property="og:image" content={pageImage} /> : null}

            {/* Twitter */}
            <meta name="twitter:card" content={pageImage ? "summary_large_image" : "summary"} />
            <meta name="twitter:title" content={pageTitle} />
            <meta name="twitter:description" content={pageDescription} />
            {pageImage ? <meta name="twitter:image" content={pageImage} /> : null}

            {jsonLd ? (
                <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
            ) : null}
        </Helmet>
    );
}
